import React from 'react';
import {useGetTrainingDataQuery} from '@api';
import {Link} from 'react-router';
import {useTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faArrowRight,
    faCircleInfo,
    faRotateRight,
} from '@fortawesome/free-solid-svg-icons';
import TrainingCountdown from '@components/training/TrainingCountdown';
import {useHeaderNavigation} from '@components/hooks/useHeaderNavigation';
import IndexSectionHeading from './IndexSectionHeading';
import type {TrainingBucketData} from '@goproblems/shared/contracts/training';

export type {TrainingData} from '@goproblems/shared/contracts/training';

interface TrainingStatusWidgetProps {
    className?: string;
    showSeeAll?: boolean;
    showInfo?: boolean;
}

const TrainingStatusWidget: React.FC<TrainingStatusWidgetProps> = ({
    className = '',
    showSeeAll = true,
    showInfo = true,
}) => {
    const {t} = useTranslation();
    const {data, error, isFetching, isLoading, refetch} =
        useGetTrainingDataQuery();
    const {handleTrainingClick} = useHeaderNavigation();

    const bucketLabels: Record<string, string> = {
        bucket_5m: '5m',
        bucket_1h: '1h',
        bucket_1d: '1d',
        bucket_1w: '1w',
        bucket_1mo: '1m',
        bucket_1y: '1y',
    };

    const renderPanel = (content: React.ReactNode) => (
        <div className={`index-home-feature h-full min-h-0 ${className}`}>
            <IndexSectionHeading
                title={t('training.spaced_repetition_title')}
                to={showSeeAll ? '/srs' : undefined}
                actions={
                    showInfo ? (
                        <Link
                            to="/article/training"
                            className="index-home-header-icon-link"
                            aria-label={`${t('training.info')} ${t('training.spaced_repetition_title')}`}
                            title={t('training.info')}>
                            <FontAwesomeIcon icon={faCircleInfo} />
                        </Link>
                    ) : undefined
                }
            />
            <div className="card index-home-feature-card training-status-card min-h-52 flex-1 site-lg:min-h-0">
                {content}
            </div>
        </div>
    );

    if (isLoading) {
        return renderPanel(
            <div className="card-body flex items-center justify-center p-3">
                <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">
                        {t('training.loading')}
                    </span>
                </div>
            </div>
        );
    }

    if (error) {
        return renderPanel(
            <div className="card-body flex items-center justify-center p-3">
                <div
                    className="rounded p-2 text-sm text-red-700 dark:text-red-300"
                    role="alert">
                    {t('training.error_loading_data')}
                    <button
                        className="btn btn-outline-danger btn-sm ms-2"
                        onClick={() => refetch()}>
                        {t('training.retry')}
                    </button>
                </div>
            </div>
        );
    }

    if (!data) return null;

    const maxValue = Math.max(
        ...Object.values(data).map(bucket => bucket.ready + bucket.notReady),
        1 // Ensure minimum scale
    );

    const totalItems = Object.values(data).reduce(
        (sum, bucket) => sum + bucket.ready,
        0
    );

    const totalItemsNotReady = Object.values(data).reduce(
        (sum, bucket) => sum + bucket.notReady,
        0
    );

    if (totalItems === 0 && totalItemsNotReady === 0) {
        return renderPanel(
            <div className="card-body flex items-center justify-center p-3">
                <div className="text-center text-sm">
                    <div className="text-neutral-500 dark:text-neutral-400">
                        {t('training.no_items_for_training')}
                        <br />
                        <Link
                            to="/problems?filters=1"
                            className="index-home-section-link mt-2">
                            {t('training.explore_problems')}{' '}
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const renderBar = (bucketKey: string, bucketData: TrainingBucketData) => {
        const total = bucketData.ready + bucketData.notReady;

        return (
            <div
                key={bucketKey}
                role="img"
                aria-label={`${bucketLabels[bucketKey]}: ${t('training.ready')} ${bucketData.ready}, ${t('training.not_ready')} ${bucketData.notReady}`}
                className="relative flex min-h-0 min-w-0 flex-1 flex-col items-center gap-1">
                <span
                    aria-hidden="true"
                    className={`text-xs tabular-nums ${total ? 'text-neutral-600 dark:text-neutral-300' : 'text-neutral-400 dark:text-neutral-500'}`}>
                    {total}
                </span>
                <div
                    aria-hidden="true"
                    className="flex min-h-0 w-full flex-1 items-end justify-center">
                    {total > 0 ? (
                        <div
                            className="flex min-h-0.5 w-3/5 max-w-8 flex-col overflow-hidden rounded-none"
                            style={{height: `${(total / maxValue) * 100}%`}}>
                            <div
                                className="bg-success"
                                style={{
                                    height: `${(bucketData.ready / total) * 100}%`,
                                }}
                            />
                            <div
                                className="bg-neutral-300 dark:bg-neutral-500"
                                style={{
                                    height: `${(bucketData.notReady / total) * 100}%`,
                                }}
                            />
                        </div>
                    ) : (
                        <div className="h-0.5 w-3/5 max-w-8 rounded-none bg-neutral-200 dark:bg-neutral-600" />
                    )}
                </div>
                <span
                    aria-hidden="true"
                    className="text-xs text-neutral-500 dark:text-neutral-400">
                    {bucketLabels[bucketKey]}
                </span>
            </div>
        );
    };

    return renderPanel(
        <div className="training-status-layout min-h-0 flex-1">
            <div className="training-status-summary gap-3 border-neutral-200 px-3 py-2.5 dark:border-neutral-700">
                <div className="min-w-0 shrink-0">
                    <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-semibold leading-none tracking-tight text-neutral-700 tabular-nums dark:text-neutral-200">
                            {totalItems}
                        </span>
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">
                            {t('training.ready')}
                        </span>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400">
                        <span className="tabular-nums">{totalItemsNotReady}</span>
                        <span>{t('training.not_ready')}</span>
                    </div>
                </div>
                {totalItems > 0 ? (
                    <button
                        type="button"
                        className="training-status-action btn btn-secondary btn-sm index-home-table-action shrink-0"
                        onClick={e => void handleTrainingClick(e)}>
                        {t('training.train')}
                    </button>
                ) : (
                    <div className="training-status-action min-w-0 text-xs text-neutral-500 dark:text-neutral-400">
                        <TrainingCountdown />
                    </div>
                )}
            </div>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 px-3 py-2.5">
                <div className="flex shrink-0 items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                        <span className="inline-flex items-center gap-1.5">
                            <span className="size-2 rounded-none bg-success" />
                            {t('training.ready')}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="size-2 rounded-none bg-neutral-300 dark:bg-neutral-500" />
                            {t('training.not_ready')}
                        </span>
                    </div>
                    <button
                        type="button"
                        className="inline-flex size-6 shrink-0 items-center justify-center rounded text-xs text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 dark:text-neutral-500 dark:hover:bg-neutral-700 dark:hover:text-neutral-300"
                        title={t('training.refresh_data')}
                        aria-label={t(
                            isFetching
                                ? 'training.refreshing'
                                : 'training.refresh_data'
                        )}
                        onClick={() => refetch()}
                        disabled={isFetching}>
                        <FontAwesomeIcon
                            icon={faRotateRight}
                            className={isFetching ? 'animate-spin' : undefined}
                        />
                    </button>
                </div>
                <div className="relative flex min-h-0 flex-1 gap-2">
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 top-5 bottom-5 flex flex-col justify-between border-b border-neutral-200 dark:border-neutral-700">
                        <div className="border-t border-dashed border-neutral-100 dark:border-neutral-700/60" />
                        <div className="border-t border-dashed border-neutral-100 dark:border-neutral-700/60" />
                        <div />
                    </div>
                    {Object.entries(data).map(([bucketKey, bucketData]) =>
                        renderBar(bucketKey, bucketData)
                    )}
                </div>
            </div>
        </div>
    );
};

export default TrainingStatusWidget;
